import { useEffect, useRef } from "react";
import { Routes, Route, NavLink, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { CalendarCheck, Users, KanbanSquare, LogOut } from "lucide-react";
import MobileToday from "@/pages/mobile/MobileToday";
import MobileCustomers from "@/pages/mobile/MobileCustomers";
import MobileCustomer from "@/pages/mobile/MobileCustomer";
import MobilePipeline from "@/pages/mobile/MobilePipeline";

function useInstallableMeta() {
  useEffect(() => {
    const added = [];
    const add = (tag, attrs) => {
      const el = document.createElement(tag);
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
      added.push(el);
    };
    const existing = document.querySelector('link[rel="manifest"]');
    const prevHref = existing?.getAttribute("href");
    if (existing) existing.setAttribute("href", "/mobilecrm.webmanifest");
    else add("link", { rel: "manifest", href: "/mobilecrm.webmanifest" });
    add("meta", { name: "apple-mobile-web-app-capable", content: "yes" });
    add("meta", { name: "mobile-web-app-capable", content: "yes" });
    add("meta", { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" });
    add("meta", { name: "apple-mobile-web-app-title", content: "Peregrine CRM" });
    const prevTitle = document.title;
    document.title = "Peregrine CRM";
    return () => {
      added.forEach((el) => el.remove());
      if (existing && prevHref) existing.setAttribute("href", prevHref);
      document.title = prevTitle;
    };
  }, []);
}

const TABS = [
  { to: "/mobilecrm", end: true, label: "Today", icon: CalendarCheck, testid: "m-tab-today" },
  { to: "/mobilecrm/customers", label: "Customers", icon: Users, testid: "m-tab-customers" },
  { to: "/mobilecrm/pipeline", label: "Pipeline", icon: KanbanSquare, testid: "m-tab-pipeline" },
];

export default function MobileCrm() {
  const { user, logout } = useAuth();
  useInstallableMeta();
  const headerRef = useRef(null);
  useEffect(() => {
    const el = headerRef.current;
    const apply = () => document.documentElement.style.setProperty("--m-header-h", `${el.offsetHeight}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div className="min-h-[100dvh] bg-[#F4F4F5] flex flex-col" data-testid="mobile-crm">
      <header ref={headerRef} className="sticky top-0 z-30 bg-[#0F172A] text-white px-4 pb-3 flex items-center gap-3" style={{ paddingTop: "max(env(safe-area-inset-top), 12px)" }}>
        <img src="/paslogo.png" alt="Peregrine Companies" className="h-8 w-auto" />
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-sm tracking-tight leading-tight">PEREGRINE CRM</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] truncate">{user?.name}</div>
        </div>
        <button onClick={logout} data-testid="m-logout" aria-label="Sign out" className="p-2 text-zinc-400 active:text-white"><LogOut className="h-5 w-5" /></button>
      </header>
      <main className="flex-1 pb-24">
        <Routes>
          <Route index element={<MobileToday />} />
          <Route path="customers" element={<MobileCustomers />} />
          <Route path="customers/:id" element={<MobileCustomer />} />
          <Route path="pipeline" element={<MobilePipeline />} />
          <Route path="*" element={<Navigate to="/mobilecrm" replace />} />
        </Routes>
      </main>
      <nav className="fixed bottom-0 inset-x-0 z-30 bg-white border-t border-zinc-200 grid grid-cols-3" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="m-tabbar">
        {TABS.map(({ to, end, label, icon: Icon, testid }) => (
          <NavLink key={to} to={to} end={end} data-testid={testid}
            className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors ${isActive ? "text-[#0F172A]" : "text-zinc-400"}`}>
            {({ isActive }) => (<><Icon className={`h-5 w-5 ${isActive ? "text-[#D4AF37]" : ""}`} />{label}</>)}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
